import i18n from 'i18next';

import { personnelTranslations, rolesTranslations, employeesTranslations } from './index';

/**
 * Register Personnel Domain Translations
 *
 * Registers all personnel domain translations (personnel, roles, employees)
 * directly into i18next instance. This makes the domain self-contained
 * and independent from /public/locales/ files.
 *
 * This function should be called once when the domain is loaded,
 * typically in the domain's main index or at app initialization.
 *
 * @example
 * // In App initialization or domain entry point
 * import { registerPersonnelTranslations } from 'domains/personnel';
 * registerPersonnelTranslations();
 */
export const registerPersonnelTranslations = (): void => {
  // Register personnel namespace
  Object.entries(personnelTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'personnel', resources, true, true);
  });

  // Register roles namespace
  Object.entries(rolesTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'roles', resources, true, true);
  });

  // Register employees namespace
  Object.entries(employeesTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'personnel-employees', resources, true, true);
  });
};

/**
 * Check if Personnel Translations are loaded
 *
 * Utility function to verify if personnel domain translations
 * have been registered in i18next.
 *
 * @param language - Language to check (defaults to current i18n language)
 * @returns {boolean} True if translations are loaded
 */
export const arePersonnelTranslationsLoaded = (language?: string): boolean => {
  const lng = language || i18n.language;
  return (
    i18n.hasResourceBundle(lng, 'personnel') &&
    i18n.hasResourceBundle(lng, 'roles') &&
    i18n.hasResourceBundle(lng, 'personnel-employees')
  );
};
